/**
 * Shared parameter table component for editing capability parameters.
 * Used by NewCapabilityWizard and EditCapabilityDialog.
 */
import { ChevronDown, ChevronRight, Plus, Trash2 } from "lucide-react";
import React from "react";
import { cn } from "src/common/utils";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select";
import { Textarea } from "src/components/ui/textarea";

import type { Parameter } from "../utils/parameterSchema";

interface ParameterRowProps {
  param: Parameter;
  depth?: number;
  expandedParams: Set<string>;
  onToggleExpand: (paramId: string) => void;
  onParameterChange: (id: string, field: keyof Parameter, value: unknown) => void;
  onRemoveParameter: (id: string) => void;
  onAddNestedParameter: (parentId: string) => void;
}

export const ParameterRow: React.FC<ParameterRowProps> = React.memo(function ParameterRow({
  param,
  depth = 0,
  expandedParams,
  onToggleExpand,
  onParameterChange,
  onRemoveParameter,
  onAddNestedParameter,
}) {
  const isExpanded = expandedParams.has(param.id);

  return (
    <>
      <tr className={cn("border-t border-border", depth > 0 && "bg-card/30")}>
        <td className="py-2 px-3">
          <div className="flex items-center gap-2" style={{ paddingLeft: `${depth * 20}px` }}>
            {param.type === "object" && (
              <button
                onClick={() => onToggleExpand(param.id)}
                className="text-muted-foreground hover:text-foreground"
                type="button"
              >
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            )}
            <Input
              value={param.name}
              onChange={(e) => onParameterChange(param.id, "name", e.target.value)}
              disabled={param.locked}
              placeholder="name"
              className={cn(
                "h-7 text-sm border-0 bg-transparent p-0 focus-visible:ring-0",
                param.locked && "text-muted-foreground"
              )}
            />
            {param.locked && <span className="text-xs text-muted-foreground">Preset</span>}
          </div>
        </td>

        <td className="py-2 px-3">
          <Select
            value={param.type}
            onValueChange={(v) => onParameterChange(param.id, "type", v)}
            disabled={param.locked}
          >
            <SelectTrigger className="h-7 text-sm border-border">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="string">String</SelectItem>
              <SelectItem value="number">Number</SelectItem>
              <SelectItem value="boolean">Boolean</SelectItem>
              <SelectItem value="select">Select</SelectItem>
              <SelectItem value="object">Object</SelectItem>
            </SelectContent>
          </Select>
        </td>

        <td className="py-2 px-3 text-center">
          <input
            type="checkbox"
            checked={param.required}
            onChange={(e) => onParameterChange(param.id, "required", e.target.checked)}
            disabled={param.locked}
            className="rounded"
          />
        </td>

        <td className="py-2 px-3">
          {param.type !== "object" && (
            <Input
              value={param.defaultValue || ""}
              onChange={(e) => onParameterChange(param.id, "defaultValue", e.target.value)}
              placeholder="-"
              className="h-7 text-sm border-border"
            />
          )}
        </td>

        <td className="py-2 px-3">
          <Textarea
            value={param.description}
            onChange={(e) => onParameterChange(param.id, "description", e.target.value)}
            placeholder="Description..."
            className="min-h-[28px] text-sm border-border resize-none"
            rows={1}
            style={{ height: "auto", minHeight: "28px" }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = "auto";
              target.style.height = target.scrollHeight + "px";
            }}
          />
        </td>

        <td className="py-2 px-3 text-right">
          {!param.locked && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemoveParameter(param.id)}
              className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
              type="button"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          )}
        </td>
      </tr>

      {param.type === "select" && (
        <tr className="border-t border-border/50">
          <td colSpan={6} className="py-2 px-3 bg-card/20">
            <div className="flex items-center gap-2 text-xs">
              <span className="text-muted-foreground">Options:</span>
              <Input
                placeholder="option1, option2, option3"
                value={param.selectOptions?.join(", ") || ""}
                onChange={(e) =>
                  onParameterChange(
                    param.id,
                    "selectOptions",
                    e.target.value.split(",").map((s) => s.trim())
                  )
                }
                className="h-7 text-sm flex-1 border-border"
              />
            </div>
          </td>
        </tr>
      )}

      {param.type === "object" && isExpanded && (
        <>
          {param.properties && param.properties.length > 0 ? (
            param.properties.map((nestedParam) => (
              <ParameterRow
                key={nestedParam.id}
                param={nestedParam}
                depth={depth + 1}
                expandedParams={expandedParams}
                onToggleExpand={onToggleExpand}
                onParameterChange={onParameterChange}
                onRemoveParameter={onRemoveParameter}
                onAddNestedParameter={onAddNestedParameter}
              />
            ))
          ) : (
            <tr className="border-t border-border/50">
              <td colSpan={6} className="py-3 px-3 text-center bg-card/20">
                <span className="text-xs text-muted-foreground">No nested properties defined</span>
                {param.customizable !== false && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onAddNestedParameter(param.id)}
                    className="ml-3 h-6 text-xs"
                    type="button"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Property
                  </Button>
                )}
              </td>
            </tr>
          )}
          {param.customizable !== false && param.properties && param.properties.length > 0 && (
            <tr className="border-t border-border/50">
              <td colSpan={6} className="py-2 px-3 bg-card/20">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onAddNestedParameter(param.id)}
                  className="h-6 text-xs text-muted-foreground"
                  type="button"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add nested property
                </Button>
              </td>
            </tr>
          )}
        </>
      )}
    </>
  );
});

interface ParameterTableProps {
  parameters: Parameter[];
  expandedParams: Set<string>;
  onToggleExpand: (paramId: string) => void;
  onParameterChange: (id: string, field: keyof Parameter, value: unknown) => void;
  onRemoveParameter: (id: string) => void;
  onAddNestedParameter: (parentId: string) => void;
  onAddParameter: () => void;
  /** Column widths - defaults optimized for full-width dialogs */
  columnWidths?: {
    name?: string;
    type?: string;
    required?: string;
    default?: string;
    actions?: string;
  };
}

export const ParameterTable: React.FC<ParameterTableProps> = ({
  parameters,
  expandedParams,
  onToggleExpand,
  onParameterChange,
  onRemoveParameter,
  onAddNestedParameter,
  onAddParameter,
  columnWidths = {},
}) => {
  const widths = {
    name: columnWidths.name ?? "w-[200px]",
    type: columnWidths.type ?? "w-[120px]",
    required: columnWidths.required ?? "w-[80px]",
    default: columnWidths.default ?? "w-[140px]",
    actions: columnWidths.actions ?? "w-[50px]",
  };

  if (parameters.length === 0) {
    return (
      <div className="text-center py-6 border border-dashed border-border rounded-lg">
        <p className="text-sm text-muted-foreground mb-2">No parameters defined yet</p>
        <Button variant="outline" size="sm" onClick={onAddParameter} type="button">
          <Plus className="h-4 w-4 mr-2" />
          Add Parameter
        </Button>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-card/50 text-xs text-muted-foreground">
          <tr>
            <th className={cn("text-left py-2 px-3 font-medium", widths.name)}>Name</th>
            <th className={cn("text-left py-2 px-3 font-medium", widths.type)}>Type</th>
            <th className={cn("text-center py-2 px-3 font-medium", widths.required)}>Required</th>
            <th className={cn("text-left py-2 px-3 font-medium", widths.default)}>Default</th>
            <th className="text-left py-2 px-3 font-medium">Description</th>
            <th className={cn("py-2 px-3", widths.actions)}></th>
          </tr>
        </thead>
        <tbody>
          {parameters.map((param) => (
            <ParameterRow
              key={param.id}
              param={param}
              expandedParams={expandedParams}
              onToggleExpand={onToggleExpand}
              onParameterChange={onParameterChange}
              onRemoveParameter={onRemoveParameter}
              onAddNestedParameter={onAddNestedParameter}
            />
          ))}
        </tbody>
      </table>
      <div className="border-t border-border p-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={onAddParameter}
          className="w-full text-xs text-muted-foreground"
          type="button"
        >
          <Plus className="h-3 w-3 mr-1" />
          Add parameter
        </Button>
      </div>
    </div>
  );
};
